import React from 'react';
import { EventContentArg } from '@fullcalendar/core';
import { Tooltip } from '@mui/material';
import { CalendarEventType, EventStatusType } from '../../../api-service/clients';

const AGENDA_EVENT_CONTENT_MAX_LENGTH = 90;
const RESPONSIBLE_USER_MAX_LENGTH = 24;

function getEventStatusBadgeText(status?: EventStatusType) {
  if (status === EventStatusType.Done) {
    return 'Udført';
  }

  if (status === EventStatusType.Cancelled) {
    return 'Aflyst';
  }

  return null;
}

function getEventStatusReason(eventInfo: EventContentArg) {
  const eventStatus = eventInfo.event.extendedProps?.eventStatus as
    | EventStatusType
    | undefined;
  const doneComment = eventInfo.event.extendedProps?.doneComment as
    | string
    | undefined;
  const cancelledComment = eventInfo.event.extendedProps?.cancelledComment as
    | string
    | undefined;

  if (eventStatus === EventStatusType.Done) {
    return doneComment?.trim();
  }

  if (eventStatus === EventStatusType.Cancelled) {
    return cancelledComment?.trim();
  }

  return undefined;
}

function truncateText(text: string, maxLength: number) {
  const normalizedText = text.trim().replace(/\s+/g, ' ');
  if (normalizedText.length <= maxLength) {
    return normalizedText;
  }

  return `${normalizedText.slice(0, maxLength).trimEnd()}...`;
}

function buildEventTooltipTitle(
  eventInfo: EventContentArg,
  badgeText: string | null,
  badgeReason?: string,
  isNexusEvent?: boolean
) {
  const locationAddress = eventInfo.event.extendedProps?.locationAddress as
    | string
    | undefined;
  const content = eventInfo.event.extendedProps?.content as string | undefined;
  const consumedMinutes = eventInfo.event.extendedProps?.consumedMinutes as
    | number
    | undefined;
  const citizenName = eventInfo.event.extendedProps?.citizenName as
    | string
    | undefined;
  const citizenCpr = eventInfo.event.extendedProps?.citizenCpr as
    | string
    | undefined;
  const responsibleUserFullName = eventInfo.event.extendedProps
    ?.responsibleUserFullName as string | undefined;
  const responsibleUserUsername = eventInfo.event.extendedProps
    ?.responsibleUserUsername as string | undefined;
  const observationTypeName = eventInfo.event.extendedProps
    ?.observationTypeName as string | undefined;
  const normalizedContent = content?.trim().replace(/\s+/g, ' ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
      {isNexusEvent && <div>Kilde: KMD Nexus</div>}
      <div style={{ fontWeight: 600 }}>{eventInfo.event.title}</div>
      {observationTypeName && <div>Observationstype: {observationTypeName}</div>}
      {citizenName && <div>Borger: {citizenName}</div>}
      {citizenCpr && <div>CPR: {citizenCpr}</div>}
      {eventInfo.timeText && <div>Tid: {eventInfo.timeText}</div>}
      {locationAddress && <div>Sted: {locationAddress}</div>}
      {Number.isFinite(consumedMinutes) && (
        <div>Brugt tid: {consumedMinutes} min.</div>
      )}
      {normalizedContent && <div>Indhold: {normalizedContent}</div>}
      {responsibleUserFullName && <div>Ansvarlig: {responsibleUserFullName}</div>}
      {responsibleUserUsername && <div>Brugernavn: {responsibleUserUsername}</div>}
      {badgeText && <div>Status: {badgeText}</div>}
      {badgeReason && <div>Kommentar: {badgeReason}</div>}
    </div>
  );
}

function NexusBadge() {
  return (
    <span style={{
      position: 'absolute',
      top: 0,
      left: 0,
      fontSize: '0.65rem',
      lineHeight: 1,
      padding: '0.2rem 0.35rem',
      borderRadius: '999px',
      border: '1px solid rgba(255,255,255,0.7)',
      backgroundColor: 'rgba(255,255,255,0.2)',
      whiteSpace: 'nowrap',
    }}>Nexus</span>
  );
}

function isCompactView(eventInfo: EventContentArg) {
  const viewType = eventInfo.view.type;
  return viewType.startsWith('dayGrid') || viewType.startsWith('multi');
}

function isDayGridDotEvent(eventInfo: EventContentArg) {
  return isCompactView(eventInfo) && !eventInfo.event.allDay;
}

export function renderServiceProviderCalendarEventContent(
  eventInfo: EventContentArg
) {
  const eventStatus = eventInfo.event.extendedProps?.eventStatus as
    | EventStatusType
    | undefined;
  const isMedicineDose =
    eventInfo.event.extendedProps?.type === CalendarEventType.Medicine;
  // En dosis "udføres" ikke — den gives; medicinsproget følger medicinkortets.
  const badgeText =
    isMedicineDose && eventStatus === EventStatusType.Done
      ? 'Givet'
      : isMedicineDose
        ? null
        : getEventStatusBadgeText(eventStatus);
  const badgeReason = getEventStatusReason(eventInfo);
  const isCancelled = eventStatus === EventStatusType.Cancelled;
  const isAgendaView = eventInfo.view.type.startsWith('list');
  const isNexusEvent =
    eventInfo.event.extendedProps?.isNexusEvent === true;
  const eventContent = eventInfo.event.extendedProps?.content as
    | string
    | undefined;
  const citizenName = eventInfo.event.extendedProps?.citizenName as
    | string
    | undefined;
  const citizenCpr = eventInfo.event.extendedProps?.citizenCpr as
    | string
    | undefined;
  const observationTypeName = eventInfo.event.extendedProps
    ?.observationTypeName as string | undefined;
  const responsibleUserFullName = eventInfo.event.extendedProps
    ?.responsibleUserFullName as string | undefined;
  const truncatedEventContent = eventContent
    ? truncateText(eventContent, AGENDA_EVENT_CONTENT_MAX_LENGTH)
    : '';
  const showAgendaContent = isAgendaView && !!truncatedEventContent;
  const tooltipTitle = buildEventTooltipTitle(
    eventInfo,
    badgeText,
    badgeReason,
    isNexusEvent
  );
  const showDot = isDayGridDotEvent(eventInfo);
  const showBadge = badgeText && !isCompactView(eventInfo);

  // Medicindoser er 30-minutters blokke med plads til én linje, så tid og titel deler den.
  // Kun starttiden vises — blokkens sluttid er ren visning og ville skubbe titlen ud.
  if (isMedicineDose && !showDot && !isAgendaView) {
    const doseTime = eventInfo.event.start?.toLocaleTimeString('da-DK', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return (
      <Tooltip title={tooltipTitle} arrow>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          {doseTime && (
            <span style={{ fontSize: '0.75rem', opacity: 0.9, flexShrink: 0 }}>
              {doseTime}
            </span>
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {eventInfo.event.title}
          </span>
          {badgeText && (
            <span
              style={{
                marginLeft: 'auto',
                flexShrink: 0,
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
            >
              ✓ {badgeText}
            </span>
          )}
        </div>
      </Tooltip>
    );
  }
  const showNexusBadge = isNexusEvent && !isCompactView(eventInfo);
  const truncatedResponsibleUser = responsibleUserFullName
    ? truncateText(responsibleUserFullName, RESPONSIBLE_USER_MAX_LENGTH)
    : null;
  const hasCitizenInfo = !!citizenName || !!citizenCpr;

  return (
    <Tooltip title={tooltipTitle} arrow>
      <div
        style={{
          display: showDot ? 'flex' : 'block',
          alignItems: showDot ? 'center' : undefined,
          gap: showDot ? '4px' : undefined,
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden',
          paddingRight: showBadge ? '3.8rem' : undefined,
          paddingLeft: showNexusBadge ? '3.8rem' : undefined,
        }}
      >
        {showDot && (
          <span
            className="fc-daygrid-event-dot"
            style={{ borderColor: eventInfo.event.borderColor, flexShrink: 0 }}
          />
        )}
        {!showDot && eventInfo.timeText && (
          <div
            style={{
              fontSize: '0.75rem',
              opacity: 0.9,
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <span style={{ flexShrink: 0 }}>{eventInfo.timeText}</span>
            {truncatedResponsibleUser && (
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity: 0.8,
                }}
              >
                ({truncatedResponsibleUser})
              </span>
            )}
          </div>
        )}
        {showDot && eventInfo.timeText && (
          <div style={{ fontSize: '0.75rem', opacity: 0.9, flexShrink: 0 }}>
            {eventInfo.timeText}
          </div>
        )}
        <div
          style={{
            textDecoration: isCancelled ? 'line-through' : undefined,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {eventInfo.event.title}
          {showAgendaContent && (
            <span style={{ fontSize: '0.85em', color: 'rgba(0, 0, 0, 0.6)' }}>
              {' - '}
              {truncatedEventContent}
            </span>
          )}
        </div>
        {observationTypeName && !showDot && !isCompactView(eventInfo) && (
          <div
            style={{
              fontSize: '0.7rem',
              opacity: 0.85,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {observationTypeName}
          </div>
        )}
        {hasCitizenInfo && !showDot && !isCompactView(eventInfo) && (
          <div
            style={{
              fontSize: '0.7rem',
              opacity: 0.85,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {citizenName}
            {citizenName && citizenCpr ? ' · ' : ''}
            {citizenCpr}
          </div>
        )}
        {showBadge && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              fontSize: '0.65rem',
              lineHeight: 1,
              padding: '0.2rem 0.35rem',
              borderRadius: '999px',
              border: isCancelled
                ? '1px solid rgba(0,0,0,0.35)'
                : '1px solid rgba(255,255,255,0.7)',
              backgroundColor: isCancelled
                ? '#ffffff'
                : 'rgba(255,255,255,0.2)',
              color: isCancelled ? '#1f1f1f' : undefined,
              whiteSpace: 'nowrap',
            }}
          >
            {badgeText}
          </span>
        )}
        {showNexusBadge && <NexusBadge />}
      </div>
    </Tooltip>
  );
}
